import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import * as Sentry from '@sentry/react';
import { locale } from 'dayjs';
import { configure } from 'mobx';
import * as React from 'react';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import DocumentDefaultRedirect from '@/components/routing/DocumentDefaultRedirect';
import OrganizationEntryRedirect from '@/components/routing/OrganizationEntryRedirect';
import { ROUTES } from '@/configs/routes';
import { cssVariablesResolver, theme } from '@/configs/theme';
import { HistoryNavigationProvider } from '@/hooks/useHistoryNavigationState';
import OrganizationDocumentsLayout from '@/layouts/OrganizationDocumentsLayout';
import OrganizationLayout from '@/layouts/OrganizationLayout';
import OrganizationNewsLayout from '@/layouts/OrganizationNewsLayout';
import NewsDetail from '@/layouts/OrganizationNewsLayout/NewsDetail';
import NewsIndex from '@/layouts/OrganizationNewsLayout/NewsIndex';
import OrganizationNoteLayout from '@/layouts/OrganizationNoteLayout';
import NoteIndex from '@/layouts/OrganizationNoteLayout/NoteIndex';
import RootLayout from '@/layouts/RootLayout';
import ErrorPage from '@/pages/ErrorPage';
import { DeviceProvider } from '@/utils/devices';
import { liveVh } from '@/utils/liveVh';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@/styles/globalStyles.scss';

import 'dayjs/locale/ru';
import Loader from './components/ui/Loader';
import { SENTRY_DSN, SENTRY_ENVIRONMENT } from './configs/env';
import FollowUpIndex from './layouts/OrganizationNoteLayout/FollowUpIndex';
import InsightIndex from './layouts/OrganizationNoteLayout/InsightIndex';

const PhoneLoginPage = React.lazy(() => import('@/pages/PhoneLoginPage'));
const TotpSetupPage = React.lazy(() => import('@/pages/TotpSetupPage'));
const PasswordResetPage = React.lazy(() => import('@/pages/PasswordResetPage'));

liveVh();

locale('ru');

configure({
  enforceActions: 'always',
  computedRequiresReaction: true,
  reactionRequiresObservable: true,
  observableRequiresReaction: true,
  useProxies: 'ifavailable',
});

if (SENTRY_DSN) {
  Sentry.setTag('application', 'front-app');
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    tracesSampleRate: 1.0,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
  });
}

const OrganizationsPage = React.lazy(() => import('@/pages/OrganizationsPage'));
const AuthCallbackAlfaPage = React.lazy(() => import('@/pages/AuthCallbackAlfa'));
const OrganizationPageDocumentsSummary = React.lazy(() => import('@/pages/OrganizationPageDocumentsSummary'));
const OrganizationPageDocumentsAiSummary = React.lazy(() => import('@/pages/OrganizationPageDocumentsAiSummary'));
const OrganizationPageDocumentProperties = React.lazy(() => import('@/pages/OrganizationPageDocumentProperties'));
const OrganizationPageDocumentsAnalyze = React.lazy(() => import('@/pages/OrganizationPageDocumentsAnalyze'));
const OrganizationPageDocumentsAnalyzeResult = React.lazy(
  () => import('@/pages/OrganizationPageDocumentsAnalyzeResult'),
);
const OrganizationPageDocumentsAnalyzeStart = React.lazy(() => import('@/pages/OrganizationPageDocumentsAnalyzeStart'));
const OrganizationPageMacroAnalyze = React.lazy(() => import('@/pages/OrganizationPageMacroAnalyze'));
const OrganizationPageAiChat = React.lazy(() => import('@/pages/OrganizationPageAiChat'));
const OrganizationPageDocumentsAnalyzeChat = React.lazy(() => import('@/pages/OrganizationPageDocumentsAnalyzeChat'));
const OrganizationPageDocumentPreview = React.lazy(() => import('@/pages/OrganizationPageDocumentPreview'));
const OrganizationPageNote = React.lazy(() => import('@/pages/OrganizationPageNote'));
const OrganizationPageNoteUpsert = React.lazy(() => import('@/pages/OrganizationPageNoteUpsert'));
const OrganizationPageInsightUpsert = React.lazy(() => import('@/pages/OrganizationPageInsightUpsert'));
const OrganizationPageInsight = React.lazy(() => import('@/pages/OrganizationPageInsight'));
const OrganizationPageFollowUp = React.lazy(() => import('@/pages/OrganizationPageFollowUp'));
const OrganizationPageFollowUpUpsert = React.lazy(() => import('@/pages/OrganizationPageFollowUpUpsert'));

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter(
  [
    {
      path: ROUTES.auth.alfaCallback.path,
      element: (
        <React.Suspense fallback={<Loader fullHeight />}>
          <AuthCallbackAlfaPage />
        </React.Suspense>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: ROUTES.auth.phoneLogin.path,
      element: (
        <React.Suspense fallback={<Loader fullHeight />}>
          <PhoneLoginPage />
        </React.Suspense>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: ROUTES.auth.totpSetup.path,
      element: (
        <React.Suspense fallback={<Loader fullHeight />}>
          <TotpSetupPage />
        </React.Suspense>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: ROUTES.auth.passwordReset.path,
      element: (
        <React.Suspense fallback={<Loader fullHeight />}>
          <PasswordResetPage />
        </React.Suspense>
      ),
      errorElement: <ErrorPage />,
    },
    { path: '/unauthorized', element: <Navigate to={ROUTES.root.path} replace /> },
    {
      element: (
        <HistoryNavigationProvider>
          <RootLayout />
        </HistoryNavigationProvider>
      ),
      children: [
        {
          index: true,
          element: <OrganizationsPage />,
        },
        {
          path: ROUTES.organization.path,
          element: <OrganizationLayout />,
          children: [
            {
              index: true,
              element: <OrganizationEntryRedirect />,
            },
            {
              path: ROUTES.organization.documents.document.analyze.start.path,
              element: <OrganizationPageDocumentsAnalyzeStart />,
            },
            {
              path: ROUTES.organization.documents.document.analyze.result.path,
              element: <OrganizationPageDocumentsAnalyzeResult />,
            },
            {
              path: ROUTES.organization.documents.document.analyze.chat.path,
              element: <OrganizationPageDocumentsAnalyzeChat />,
            },
            {
              path: ROUTES.organization.macro.analyze.chat.path,
              element: <OrganizationPageDocumentsAnalyzeChat />,
            },
            {
              path: ROUTES.organization.macro.analyze.result.path,
              element: <OrganizationPageDocumentsAnalyzeResult />,
            },
            {
              path: ROUTES.organization.macro.path,
              element: <OrganizationPageMacroAnalyze />,
            },
            {
              path: ROUTES.organization.documents.path,
              element: <OrganizationDocumentsLayout />,
              children: [
                {
                  index: true,
                  element: <OrganizationPageAiChat />,
                },
                {
                  path: ROUTES.organization.documents.chat.path,
                  element: <OrganizationPageAiChat />,
                },

                {
                  path: ROUTES.organization.documents.document.relativePath,
                  children: [
                    {
                      index: true,
                      element: <DocumentDefaultRedirect />,
                    },
                    {
                      path: ROUTES.organization.documents.document.summary.path,
                      element: <OrganizationPageDocumentsSummary />,
                    },
                    {
                      path: ROUTES.organization.documents.document.aiSummary.path,
                      element: <OrganizationPageDocumentsAiSummary />,
                    },
                    {
                      path: ROUTES.organization.documents.document.properties.path,
                      element: <OrganizationPageDocumentProperties />,
                    },
                    {
                      path: ROUTES.organization.documents.document.analyze.path,
                      element: <OrganizationPageDocumentsAnalyze />,
                    },
                    {
                      path: ROUTES.organization.documents.document.documentPreview.path,
                      element: <OrganizationPageDocumentPreview />,
                    },
                  ],
                },
              ],
            },
            {
              path: ROUTES.organization.news.path,
              element: <OrganizationNewsLayout />,
              children: [
                {
                  index: true,
                  element: <NewsIndex />,
                },
                {
                  path: ROUTES.organization.news.article.relativePath,
                  element: <NewsDetail />,
                },
              ],
            },
            {
              path: ROUTES.organization.notebook.path,
              element: <OrganizationNoteLayout />,
              children: [
                {
                  index: true,
                  element: <Navigate to={ROUTES.organization.notebook.notes.relativePath} replace />,
                },
                {
                  path: ROUTES.organization.notebook.notes.relativePath,
                  children: [
                    {
                      index: true,
                      element: <NoteIndex />,
                    },
                    {
                      path: ROUTES.organization.notebook.notes.createNote.relativePath,
                      element: <OrganizationPageNoteUpsert />,
                    },
                    {
                      path: ROUTES.organization.notebook.notes.note.relativePath,
                      element: <OrganizationPageNote />,
                    },
                    {
                      path: ROUTES.organization.notebook.notes.note.edit.path,
                      element: <OrganizationPageNoteUpsert />,
                    },
                  ],
                },
                {
                  path: ROUTES.organization.notebook.insights.relativePath,
                  children: [
                    {
                      index: true,
                      element: <InsightIndex />,
                    },
                    {
                      path: ROUTES.organization.notebook.insights.createInsight.relativePath,
                      element: <OrganizationPageInsightUpsert />,
                    },
                    {
                      path: ROUTES.organization.notebook.insights.insight.relativePath,
                      element: <OrganizationPageInsight />,
                    },
                    {
                      path: ROUTES.organization.notebook.insights.insight.edit.path,
                      element: <OrganizationPageInsightUpsert />,
                    },
                  ],
                },
                {
                  path: ROUTES.organization.notebook.followUps.relativePath,
                  children: [
                    {
                      index: true,
                      element: <FollowUpIndex />,
                    },
                    {
                      path: ROUTES.organization.notebook.followUps.createFollowUp.relativePath,
                      element: <OrganizationPageFollowUpUpsert />,
                    },
                    {
                      path: ROUTES.organization.notebook.followUps.followUp.relativePath,
                      element: <OrganizationPageFollowUp />,
                    },
                    {
                      path: ROUTES.organization.notebook.followUps.followUp.edit.path,
                      element: <OrganizationPageFollowUpUpsert />,
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
      errorElement: <ErrorPage />,
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

createRoot(document.getElementById('root') as HTMLElement).render(
  <MantineProvider theme={theme} cssVariablesResolver={cssVariablesResolver}>
    <DeviceProvider>
      <DatesProvider settings={{ locale: 'ru' }}>
        <RouterProvider
          router={router}
          future={{
            v7_startTransition: true,
          }}
        />
      </DatesProvider>
    </DeviceProvider>
  </MantineProvider>,
);
